import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" }
const _hoisted_2 = { class: "flex items-center gap-2 text-sm mb-6" }
const _hoisted_3 = { class: "text-[#22282F] font-medium" }
const _hoisted_4 = { class: "bg-gradient-to-r from-[#22282F] to-[#22282F]/80 text-white rounded-xl p-6 mb-6 shadow-lg" }
const _hoisted_5 = { class: "flex items-center justify-between" }
const _hoisted_6 = { class: "flex items-center gap-4" }
const _hoisted_7 = { class: "p-3 bg-white/10 rounded-lg" }
const _hoisted_8 = { class: "text-2xl font-bold" }
const _hoisted_9 = { class: "text-white/70 text-sm" }
const _hoisted_10 = { class: "mb-6 bg-white border border-gray-200 rounded-xl p-4 shadow-sm" }
const _hoisted_11 = { class: "relative" }
const _hoisted_12 = {
  key: 0,
  class: "flex items-center justify-center py-12 bg-white border border-gray-200 rounded-xl"
}
const _hoisted_13 = {
  key: 1,
  class: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
}
const _hoisted_14 = ["onClick"]
const _hoisted_15 = { class: "flex items-start justify-between mb-4" }
const _hoisted_16 = { class: "p-3 bg-gray-50 rounded-lg group-hover:bg-[#22282F] group-hover:text-white transition-colors" }
const _hoisted_17 = { class: "flex gap-2" }
const _hoisted_18 = ["onClick"]
const _hoisted_19 = ["onClick"]
const _hoisted_20 = { class: "text-lg font-semibold text-[#22282F] mb-1" }
const _hoisted_21 = { class: "text-sm text-gray-500 mb-4" }
const _hoisted_22 = { class: "flex items-center justify-between pt-4 border-t border-gray-100" }
const _hoisted_23 = { class: "flex items-center gap-2 text-sm text-gray-600" }
const _hoisted_24 = {
  key: 2,
  class: "bg-white border border-gray-200 rounded-xl p-12 text-center"
}
const _hoisted_25 = { class: "text-sm text-gray-500" }
const _hoisted_26 = { class: "bg-white rounded-xl max-w-md w-full p-6" }
const _hoisted_27 = { class: "text-lg font-bold text-[#22282F] mb-4" }
const _hoisted_28 = { class: "space-y-4" }
const _hoisted_29 = { class: "flex gap-3 mt-6" }

import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { Building2, Layers, Users, Plus, Edit3, Trash2, Search, ChevronRight } from 'lucide-vue-next'
import AdminLayout from '@/layouts/AdminLayout.vue'
import { apiFetch } from '@/utils/apiFetch'
import { useToast } from '@/utils/toast'

interface Direction {
  id: number
  code: string
  nom: string
}

interface Departement {
  id: number
  code: string
  nom: string
  services_count?: number
  direction: Direction
}


export default /*@__PURE__*/_defineComponent({
  __name: 'DepartementsListView',
  setup(__props) {

const toast = useToast()

const router = useRouter()
const route = useRoute()
const directionId = ref(Number(route.params.directionId))

const loading = ref(false)
const direction = ref<Direction | null>(null)
const departements = ref<Departement[]>([])
const searchQuery = ref('')
const showModal = ref(false)
const editingDepartement = ref<Departement | null>(null)
const form = ref({ code: '', nom: '' })

const filteredDepartements = computed(() => {
  if (!searchQuery.value) return departements.value
  const query = searchQuery.value.toLowerCase()
  return departements.value.filter(d =>
    d.nom.toLowerCase().includes(query) || d.code.toLowerCase().includes(query)
  )
})

const fetchDirection = async () => {
  try {
    direction.value = await apiFetch(`/api/admin/directions/${directionId.value}/`)
  } catch (error: any) {
    toast.error('Erreur', 'Impossible de charger la direction')
  }
}

const fetchDepartements = async () => {
  loading.value = true
  try {
    const allDepartements = await apiFetch('/api/admin/departements/')
    departements.value = allDepartements.filter((d: Departement) => d.direction?.id === directionId.value)
  } catch (error: any) {
    toast.error('Erreur', 'Impossible de charger les départements')
  } finally {
    loading.value = false
  }
}

const openCreateModal = () => {
  editingDepartement.value = null
  form.value = { code: '', nom: '' }
  showModal.value = true
}

const openEditModal = (departement: Departement) => {
  editingDepartement.value = departement
  form.value = { code: departement.code, nom: departement.nom }
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  editingDepartement.value = null
}

const saveDepartement = async () => {
  try {
    const payload = {
      ...form.value,
      direction_id: directionId.value
    }

    const url = editingDepartement.value
      ? `/api/admin/departements/${editingDepartement.value.id}/update/`
      : '/api/admin/departements/create/'

    await apiFetch(url, {
      method: editingDepartement.value ? 'PUT' : 'POST',
      body: JSON.stringify(payload)
    })

    toast.success(
      editingDepartement.value ? 'Département modifié' : 'Département créé',
      'L\'opération a réussi'
    )
    await fetchDepartements()
    closeModal()
  } catch (error: any) {
    toast.error('Erreur', error.message || 'Impossible de sauvegarder le département')
  }
}

const deleteDepartement = async (id: number) => {
  try {
    await apiFetch(`/api/admin/departements/${id}/delete/`, {
      method: 'DELETE'
    })

    toast.success('Département supprimé', 'Le département a été supprimé avec succès')
    await fetchDepartements()
  } catch (error: any) {
    toast.error('Erreur', error.message || 'Impossible de supprimer le département')
  }
}

const goToServices = (departement: Departement) => {
  router.push(`/organisation/directions/${directionId.value}/departements/${departement.id}/services`)
}

onMounted(() => {
  fetchDirection()
  fetchDepartements()
})

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createBlock(AdminLayout, null, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("nav", _hoisted_2, [
          _createVNode(_component_router_link, {
            to: "/organisation",
            class: "text-gray-500 hover:text-[#22282F] transition-colors"
          }, {
            default: _withCtx(() => _cache[3] || (_cache[3] = [
              _createTextVNode(" Organisation ")
            ])),
            _: 1,
            __: [3]
          }),
          _createVNode(_unref(ChevronRight), { class: "w-4 h-4 text-gray-400" }),
          _createElementVNode("span", _hoisted_3, _toDisplayString(direction.value?.nom), 1)
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("div", _hoisted_7, [
                _createVNode(_unref(Building2), { class: "w-8 h-8" })
              ]),
              _createElementVNode("div", null, [
                _createElementVNode("h1", _hoisted_8, _toDisplayString(direction.value?.nom), 1),
                _createElementVNode("p", _hoisted_9, _toDisplayString(direction.value?.code), 1)
              ])
            ]),
            _createElementVNode("button", {
              onClick: openCreateModal,
              class: "px-4 py-2 bg-white text-[#22282F] rounded-lg hover:bg-white/90 transition-colors flex items-center gap-2"
            }, [
              _createVNode(_unref(Plus), { class: "w-4 h-4" }),
              _cache[4] || (_cache[4] = _createTextVNode(" Nouveau Département "))
            ])
          ])
        ]),
        _createElementVNode("div", _hoisted_10, [
          _createElementVNode("div", _hoisted_11, [
            _createVNode(_unref(Search), { class: "absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" }),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
              type: "text",
              placeholder: "Rechercher un département par nom ou code...",
              class: "w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20"
            }, null, 512), [
              [_vModelText, searchQuery.value]
            ])
          ])
        ]),
        (loading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_12, _cache[5] || (_cache[5] = [
              _createElementVNode("div", { class: "w-8 h-8 border-4 border-gray-200 border-t-[#22282F] rounded-full animate-spin" }, null, -1)
            ])))
          : (filteredDepartements.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredDepartements.value, (departement) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: departement.id,
                    class: "bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer group",
                    onClick: ($event: any) => (goToServices(departement))
                  }, [
                    _createElementVNode("div", _hoisted_15, [
                      _createElementVNode("div", _hoisted_16, [
                        _createVNode(_unref(Layers), { class: "w-6 h-6" })
                      ]),
                      _createElementVNode("div", _hoisted_17, [
                        _createElementVNode("button", {
                          onClick: _withModifiers(($event: any) => (openEditModal(departement)), ["stop"]),
                          class: "p-2 hover:bg-gray-100 rounded-lg transition-colors",
                          title: "Modifier"
                        }, [
                          _createVNode(_unref(Edit3), { class: "w-4 h-4 text-gray-600" })
                        ], 8, _hoisted_18),
                        _createElementVNode("button", {
                          onClick: _withModifiers(($event: any) => (deleteDepartement(departement.id)), ["stop"]),
                          class: "p-2 hover:bg-red-50 rounded-lg transition-colors",
                          title: "Supprimer"
                        }, [
                          _createVNode(_unref(Trash2), { class: "w-4 h-4 text-red-600" })
                        ], 8, _hoisted_19)
                      ])
                    ]),
                    _createElementVNode("h3", _hoisted_20, _toDisplayString(departement.nom), 1),
                    _createElementVNode("p", _hoisted_21, _toDisplayString(departement.code), 1),
                    _createElementVNode("div", _hoisted_22, [
                      _createElementVNode("div", _hoisted_23, [
                        _createVNode(_unref(Users), { class: "w-4 h-4" }),
                        _createElementVNode("span", null, _toDisplayString(departement.services_count || 0) + " service(s)", 1)
                      ]),
                      _createVNode(_unref(ChevronRight), { class: "w-5 h-5 text-gray-400 group-hover:text-[#22282F] transition-colors" })
                    ])
                  ], 8, _hoisted_14))
                }), 128))
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_24, [
                _createVNode(_unref(Layers), { class: "w-16 h-16 text-gray-300 mx-auto mb-4" }),
                _cache[6] || (_cache[6] = _createElementVNode("h3", { class: "text-lg font-semibold text-[#22282F] mb-2" }, "Aucun département trouvé", -1)),
                _createElementVNode("p", _hoisted_25, _toDisplayString(searchQuery.value ? 'Essayez de modifier votre recherche' : 'Créez le premier département de cette direction'), 1)
              ])),
        (showModal.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 3,
              class: "fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50",
              onClick: _withModifiers(closeModal, ["self"])
            }, [
              _createElementVNode("div", _hoisted_26, [
                _createElementVNode("h3", _hoisted_27, _toDisplayString(editingDepartement.value ? 'Modifier le département' : 'Nouveau département'), 1),
                _createElementVNode("div", _hoisted_28, [
                  _createElementVNode("div", null, [
                    _cache[7] || (_cache[7] = _createElementVNode("label", { class: "block text-sm font-medium text-[#22282F] mb-2" }, "Code", -1)),
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.value.code) = $event)),
                      type: "text",
                      class: "w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20",
                      placeholder: "ex: DEPT-01"
                    }, null, 512), [
                      [_vModelText, form.value.code]
                    ])
                  ]),
                  _createElementVNode("div", null, [
                    _cache[8] || (_cache[8] = _createElementVNode("label", { class: "block text-sm font-medium text-[#22282F] mb-2" }, "Nom", -1)),
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.value.nom) = $event)),
                      type: "text",
                      class: "w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20",
                      placeholder: "ex: Département Administratif"
                    }, null, 512), [
                      [_vModelText, form.value.nom]
                    ])
                  ])
                ]),
                _createElementVNode("div", _hoisted_29, [
                  _createElementVNode("button", {
                    onClick: closeModal,
                    class: "flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  }, " Annuler "),
                  _createElementVNode("button", {
                    onClick: saveDepartement,
                    class: "flex-1 px-4 py-2 bg-[#22282F] text-white rounded-lg hover:bg-[#22282F]/90 transition-colors"
                  }, _toDisplayString(editingDepartement.value ? 'Modifier' : 'Créer'), 1)
                ])
              ])
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }))
}
}

})